/**
 * Signing in.
 *
 * The only screen served to somebody who is not signed in, and the only one
 * that renders without the shell.
 *
 * **One message for every rejection.** "No such user" and "wrong password" told
 * apart is a way to find out who has an account here, and this panel has a
 * handful of accounts that all belong to the same organisation.
 *
 * There is no rate limiting in this file, and that is not an omission: GoTrue
 * does it, which is one of the reasons `login-attempts.ts` did not come across.
 */

import { el, replace } from '../dom.ts';
import { translate as t, translateKey } from '../i18n.ts';
import { signIn } from '../session.ts';

export function renderLogin(root: Element, onSignedIn: () => void, reason?: string): void {
  const problem = el('p', { class: 'finding error', role: 'alert', hidden: true });
  const submit = el('button', { type: 'submit' }, t('login.submit'));

  if (reason !== undefined) {
    problem.textContent = translateKey(reason);
    problem.removeAttribute('hidden');
  }

  const email = el('input', { type: 'email', name: 'email', id: 'email', autocomplete: 'username', required: true });
  const password = el('input', {
    type: 'password',
    name: 'password',
    id: 'password',
    autocomplete: 'current-password',
    required: true,
  });

  const form = el(
    'form',
    { class: 'card login', method: 'post', novalidate: true },
    el('h1', {}, t('login.title')),
    problem,
    el('label', { for: 'email' }, t('login.email')),
    email,
    el('label', { for: 'password' }, t('login.password')),
    password,
    el('div', { class: 'actions' }, submit),
  );

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (email.value.trim() === '' || password.value === '') {
      show(problem, t('login.error.missing'));
      return;
    }

    // Disabled while the request is in flight: a second press posts the same
    // credentials again and GoTrue counts it as a second failed attempt.
    submit.disabled = true;
    problem.setAttribute('hidden', '');

    void signIn(email.value.trim(), password.value)
      .then((result) => {
        if ('messageKey' in result) {
          show(problem, translateKey(result.messageKey));
          password.value = '';
          password.focus();
          return;
        }
        onSignedIn();
      })
      .catch(() => show(problem, t('app.loadFailed')))
      .finally(() => {
        submit.disabled = false;
      });
  });

  replace(root, form);
  document.title = `${t('login.title')} — ${t('app.name')}`;
  email.focus();
}

function show(problem: HTMLElement, message: string): void {
  problem.textContent = message;
  problem.removeAttribute('hidden');
}
